import { Palette, StackBarGraph, TableSummary, Tokens } from "@homebound/beam";
import { CostCodeBidSummaryStatus } from "src/generated/graphqlTypes";
import { queryResult } from "src/utils";
import {
  BidPackagesPageV2_FlagItemFragment,
  useBidPackagesPageV2CostCodeSummaryQuery,
} from "./BidPackagesPageV2.generated";
import { uncoveredFlags } from "./utils";

type CostCodeCoverageSummaryProps = {
  projectId: string;
  onIssuesClick: () => void;
  onMissingClick: () => void;
  onIncompleteClick: () => void;
  onWarningsClick: () => void;
};

export function CostCodeCoverageSummary(props: CostCodeCoverageSummaryProps) {
  const { projectId, onIssuesClick, onMissingClick, onIncompleteClick, onWarningsClick } = props;
  const query = useBidPackagesPageV2CostCodeSummaryQuery({ variables: { projectId } });

  return queryResult(query, {
    data: ({ community }) => {
      const { flagItems, statusItems, totalFlags, totalStatuses } = community.costCodeBidSummaryReport;
      const summaryMetrics = [
        {
          label: "Missing",
          count: flagItemCount(flagItems, isMissingItem),
          icon: "xCircle" as const,
          color: Tokens.Danger,
          onClick: onMissingClick,
        },
        {
          label: "Incomplete",
          count: flagItemCount(flagItems, isIncompleteItem),
          icon: "error" as const,
          color: Palette.Orange500,
          onClick: onIncompleteClick,
        },
        {
          label: "Warnings",
          count: flagItemCount(flagItems, isWarningItem),
          icon: "errorCircle" as const,
          color: Palette.Yellow500,
          onClick: onWarningsClick,
        },
      ].filter((metric) => metric.count > 0);
      const barGraphSegments = statusItems
        .sortBy((statusItem) => statusSortOrder.indexOf(statusItem.status.code))
        .map((statusItem) => ({
          label: statusItem.status.name,
          count: statusItem.count,
          color: statusColorMap[statusItem.status.code],
        }));

      return (
        <TableSummary
          title="Cost Code Coverage"
          metrics={summaryMetrics}
          action={
            summaryMetrics.nonEmpty
              ? { label: `View ${totalFlags} Issues`, mobileLabel: "Issues", onClick: onIssuesClick }
              : undefined
          }
          footer={
            <StackBarGraph
              title="Coverage by Status"
              totalLabel={`${totalStatuses} Cost Codes`}
              segments={barGraphSegments}
            />
          }
        />
      );
    },
  });
}

const statusColorMap = {
  [CostCodeBidSummaryStatus.Complete]: Palette.Green500,
  [CostCodeBidSummaryStatus.InProgress]: Palette.Gray500,
  [CostCodeBidSummaryStatus.Incomplete]: Palette.Orange500,
  [CostCodeBidSummaryStatus.Missing]: Palette.Red500,
};

const statusSortOrder = [
  CostCodeBidSummaryStatus.Complete,
  CostCodeBidSummaryStatus.InProgress,
  CostCodeBidSummaryStatus.Incomplete,
  CostCodeBidSummaryStatus.Missing,
];

function flagItemCount(
  flagItems: BidPackagesPageV2_FlagItemFragment[],
  matches: (item: BidPackagesPageV2_FlagItemFragment) => boolean,
): number {
  return flagItems.find(matches)?.count ?? 0;
}

/** matches the BE's uncovered flags group. */
function isMissingItem(item: BidPackagesPageV2_FlagItemFragment): boolean {
  return item.flags.some((flag) => uncoveredFlags.includes(flag.code));
}

/** matches the BE's incomplete flags group. */
function isIncompleteItem(item: BidPackagesPageV2_FlagItemFragment): boolean {
  return item.flags.some((flag) => flag.isLevelOne && !uncoveredFlags.includes(flag.code));
}

/** matches the BE's warnings flags group. */
function isWarningItem(item: BidPackagesPageV2_FlagItemFragment): boolean {
  return item.flags.some((flag) => flag.isLevelTwo);
}
